import React, {
  MouseEvent,
  TouchEvent,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react"
import styled, { css } from "styled-components"
import Arrow from "../../../../../images/arrow.inline.svg"

import { renderRichText } from "gatsby-source-contentful/rich-text"
import {
  AppFunctionComponent,
  ListItemBuilder,
  RenderRichText,
} from "../../../../../types"
import { ScrollAnchor } from "../../custom/innovation-lab-form/innovation-lab.component"
import { SliderButtonStyle } from "./reveal-list.styled"
import {
  ControlsNextIcon,
  ControlsPrevIcon,
} from "../../../slider/slider-arrows-text.component"
import useTranslationHook from "../../../../../hooks/use-translation.hook"
import { Slide } from "../../../slider-item.component"
import ListItemRefactored from "../../../list-item-refactored.component"
import { BasicButtonLink, InvertedButtonLink } from "../../../../../ui"

const sliderLayoutBreakpoint = 440

const SliderWrapper = styled.div`
  padding: 0;
  overflow: hidden;
  position: relative;
  background: #f4f5f7;
  padding: 64px 0;

  @media (min-width: 768px) {
    padding: 112px 0;
  }
`

const Slider = styled.div`
  margin: 0 auto;
  max-width: 1260px;
  width: calc(100% - 30px);
  @media (min-width: 768px) {
    width: calc(100% - 60px);
  }
`

const SliderText = styled.div`
  margin: 0 auto;
  max-width: 1260px;
  width: calc(100% - 30px);
  margin-bottom: 48px;

  @media (min-width: 768px) {
    width: calc(100% - 60px);
    margin-bottom: 80px;
  }
  h2 {
    margin-top: 0;
    font-family: Facto;
    font-size: 23px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 27.6px */
  }
`

// Mobile slider track - only used on mobile
const SliderTrack = styled.div<{
  currentSlideIndex: number
  itemsPerColumn: number
}>`
  width: 100%;
  display: flex;
  user-select: none;
  flex-direction: row;
  transition: transform 0.5s ease-in-out;

  @media (max-width: 767px) {
    ${({ currentSlideIndex }) => css`
      --position-Y: ${currentSlideIndex}px;
      --position-X: calc(
        -${currentSlideIndex * 100}% - ${currentSlideIndex * 15}px
      );
    `};
    transform: translate3d(var(--position-X), 0, var(--position-Y));
  }

  @media (min-width: 768px) {
    display: none; /* Hide slider track on desktop */
  }
`

// Desktop grid container
const DesktopGrid = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 80px;
    width: 100%;
    justify-items: start;
    align-items: start;
    overflow: hidden;
  }
`

// Desktop grid item
const DesktopGridItem = styled.div<{
  style?: ListItemBuilder["style"]
  shouldShowLine?: boolean
}>`
  display: flex;
  flex-direction: column;
  position: relative;

  /* Add line after first item of each row (except last row) */
  &::after {
    content: "";
    position: absolute;
    bottom: -40px;
    left: 0;
    right: 0;
    width: 100vw;
    height: 0.5px;
    background-color: #d4d9e4;
    transform: translateX(calc(-50vw + 50%));
    display: ${({ shouldShowLine }) => (shouldShowLine ? "block" : "none")};
  }

  /* Add bottom margin to create space for the line */
  margin-bottom: 80px;

  &:last-child {
    margin-bottom: 0;
  }

  h2,
  p {
    padding-left: 0;
    padding-right: 0;
  }

  h2 {
    font-family: Facto;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%;
  }

  p {
    color: #5b5b5b;
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }

  ${({ style }) =>
    style?.text_align === "center" &&
    css`
      text-align: center;
    `}

  ${({ style }) =>
    style?.image_height &&
    css`
      img {
        height: ${style.image_height} !important;
        min-height: ${style.image_height} !important;
        margin-bottom: 48px;
      }
    `}
`

// Mobile slider item container (column)
const SlideItem = styled.div<{ style?: ListItemBuilder["style"] }>`
  cursor: grab;
  user-select: none;

  @media (max-width: 767px) {
    min-width: 100%;
    max-width: 100%;
    margin-right: 15px;
    box-sizing: border-box;
    display: flex;
    gap: 48px;
    flex-direction: column;
    align-items: center;
  }

  @media (min-width: 768px) {
    display: none; /* Hide on desktop since we use grid */
  }

  h2,
  p {
    padding-left: 0;
    padding-right: 0;
  }

  h2 {
    font-family: Facto;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  p {
    color: #5b5b5b;
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }

  ${({ style }) =>
    style?.text_align === "center" &&
    css`
      text-align: center;
    `}

  ${({ style }) =>
    style?.image_height &&
    css`
      img {
        height: ${style.image_height} !important;
        min-height: ${style.image_height} !important;
        margin-bottom: 48px;
      }
    `}
`

// Mobile column item styling
const ColumnDisplayItem = styled(ListItemRefactored)<{
  style?: ListItemBuilder["style"]
}>`
  ${({ style }) =>
    style?.image_height &&
    css`
      img {
        height: ${style.image_height} !important;
        min-height: ${style.image_height} !important;
        margin-bottom: 48px;
      }
    `}
  @media (max-width: 767px) {
    width: 100%;
    max-width: none;
    min-width: auto;
    margin-right: 0;
    margin-bottom: 20px;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    grid-column-gap: 24px;
    grid-template-areas:
      "Image Heading"
      "Image Text";

    &:last-child {
      margin-bottom: 0;
    }

    h2,
    p {
      padding-left: 0;
      padding-right: 0;
      text-align: left;
    }

    h2 {
      grid-area: Heading;
    }
    p {
      grid-area: Text;
    }
    img {
      margin-bottom: 0 !important;
      height: 48px !important;
      min-height: 48px !important;
    }
  }

  @media (min-width: 768px) {
    display: none; /* Hide on desktop */
  }
`

// Desktop item wrapper for SliderItem component
const DesktopSliderItem = styled(ListItemRefactored)<{
  style?: ListItemBuilder["style"]
}>`
  width: 100%;

  img {
    height: 80px !important;
    min-height: 80px !important;
    margin-bottom: 48px;
  }
`

export const ArrowIcon = styled(Arrow)`
  height: 15px;
  user-selectable: none;
  @media (min-width: 960px) {
    height: 22px;
  }
`

export const ArrowContainer = styled.div<{ active: boolean }>`
  opacity: 1;
  width: 32px;
  height: 32px;
  display: flex;
  cursor: pointer;
  border-radius: 50%;
  align-items: center;
  flex-direction: row;
  pointer-events: all;
  visibility: visible;
  user-selectable: none;
  justify-content: center;
  background-color: ${({ theme }) => theme.color.arrowBackgroundDark};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;

  @media (min-width: 960px) {
    width: 50px;
    height: 50px;
  }

  ${({ active }) =>
    !active &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`

export const ArrowPrev = styled(ArrowContainer)`
  svg {
    transform: rotateZ(180deg);
  }
`

export const ArrowNext = styled(ArrowContainer)``

export const ArrowsWrapper = styled.div`
  display: flex;
  max-width: 1260px;
  margin: 0 auto;
  flex-direction: row;
  pointer-events: none;
  width: calc(100vw - 60px);
  justify-content: space-between;

  @media (min-width: ${sliderLayoutBreakpoint}px) {
  }

  @media (min-width: 768px) {
  }

  @media (min-width: 960px) {
  }

  @media (min-width: 1240px) {
  }

  @media (min-width: 1430px) {
  }
`

export const StyledControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 48px;
  padding-top: 48px;
  border-top: 0.5px solid #d4d9e4;
  gap: 24px;

  margin-left: 20px;
  margin-right: 20px;

  @media (min-width: 768px) {
    display: none; /* Hide controls on desktop */
  }
`

interface Props {
  slides?: Record<string, any>[]
  className?: string
  anchorId?: string
  text?: RenderRichText
  bottomButtonText?: string
  bottomButtonLink?: string
  secondBottomButtonText?: string
  secondBottomButtonLink?: string
}

const RevealList: AppFunctionComponent<Props> = ({
  className,
  slides = [],
  text,
  anchorId,
  bottomButtonText,
  bottomButtonLink,
  secondBottomButtonText,
  secondBottomButtonLink,
}) => {
  const windowAvailable = typeof window !== `undefined`
  const { translate } = useTranslationHook()
  const ITEMS_PER_COLUMN_MOBILE = 3

  const [isMobile, setIsMobile] = useState(
    windowAvailable ? window.innerWidth < 768 : false
  )

  // Recalculate on resize
  useEffect(() => {
    if (!windowAvailable) return
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [windowAvailable])

  const calculateLastPossibleSlideIndex = useCallback(() => {
    if (isMobile) {
      if (!slides || slides.length === 0) return 0
      return Math.ceil(slides.length / ITEMS_PER_COLUMN_MOBILE) - 1
    }
    // Desktop doesn't need this anymore since there's no slider
    return 0
  }, [isMobile, slides, ITEMS_PER_COLUMN_MOBILE])

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [lastPossibleSlideIndex, setLastPossibleSlideIndex] = useState(
    calculateLastPossibleSlideIndex()
  )

  // Effect to reset slide index and update last possible index when view mode or slides change
  useEffect(() => {
    setCurrentSlideIndex(0)
    setLastPossibleSlideIndex(calculateLastPossibleSlideIndex())
  }, [isMobile, slides, calculateLastPossibleSlideIndex])

  const slide = (i: number) => {
    setCurrentSlideIndex(i)
  }

  const prev = () => {
    const newIndex =
      currentSlideIndex > 0 ? currentSlideIndex - 1 : currentSlideIndex
    slide(newIndex)
  }

  const next = () => {
    const lastSlideIndex = calculateLastPossibleSlideIndex()
    const newIndex =
      currentSlideIndex < lastSlideIndex
        ? currentSlideIndex + 1
        : currentSlideIndex
    setLastPossibleSlideIndex(lastSlideIndex)
    slide(newIndex)
  }

  let touchStartX = 0
  let touchStartY = 0

  const touchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX = e.touches[0].clientX
    touchStartY = e.touches[0].clientY
  }

  const touchMove = (e: TouchEvent<HTMLDivElement>) => {
    const touchEndX = e.touches[0].clientX
    const touchEndY = e.touches[0].clientY

    const diffX = Math.abs(touchStartX - touchEndX)
    const diffY = Math.abs(touchStartY - touchEndY)

    if (diffX > 40 && diffY < 30) {
      touchEndX - touchStartX > 0 ? prev() : next()
    }
  }

  let mouseStartX = 0
  let mouseEndX = 0

  const mouseDown = (e: MouseEvent<HTMLDivElement>) => {
    mouseStartX = e.clientX
  }

  const mouseUp = (e: MouseEvent<HTMLDivElement>) => {
    mouseEndX = e.clientX
    const diffX = Math.abs(mouseStartX - mouseEndX)
    if (diffX > (isMobile ? 50 : 100)) {
      mouseEndX - mouseStartX > 0 ? prev() : next()
    }
  }

  const displayableSlides = useMemo(() => {
    if (isMobile) {
      const result = []
      if (!slides || slides.length === 0) return []
      for (let i = 0; i < slides.length; i += ITEMS_PER_COLUMN_MOBILE) {
        result.push(slides.slice(i, i + ITEMS_PER_COLUMN_MOBILE))
      }
      return result
    }
    return []
  }, [slides, isMobile, ITEMS_PER_COLUMN_MOBILE])

  return (
    <SliderWrapper
      className={className}
      onMouseDown={isMobile ? mouseDown : undefined}
      onMouseUp={isMobile ? mouseUp : undefined}
      onTouchStart={isMobile ? touchStart : undefined}
      onTouchMove={isMobile ? touchMove : undefined}
    >
      {text && <SliderText>{renderRichText(text)}</SliderText>}
      <ScrollAnchor id={anchorId} />
      <Slider>
        {/* Mobile Slider */}
        <SliderTrack
          currentSlideIndex={currentSlideIndex}
          itemsPerColumn={ITEMS_PER_COLUMN_MOBILE}
        >
          {displayableSlides.map(
            (slideColumn: Slide[], columnIndex: number) => (
              <SlideItem key={`column-${columnIndex}`}>
                {slideColumn.map((slideItemData: Slide, itemIndex: number) => (
                  <ColumnDisplayItem
                    slide={slideItemData as ListItemBuilder}
                    key={`mobile-item-${columnIndex}-${itemIndex}`}
                    style={slideItemData.style}
                    className="column-inner-item"
                  />
                ))}
              </SlideItem>
            )
          )}
        </SliderTrack>

        {/* Desktop Grid */}
        <DesktopGrid>
          {slides.map((slideItemData: Slide, index: number) => {
            const totalItems = slides.length
            const itemsInLastRow = totalItems % 3 === 0 ? 3 : totalItems % 3
            const isLastRow = index >= totalItems - itemsInLastRow
            const isFirstItemInRow = index % 3 === 0
            const shouldShowLine = isFirstItemInRow && !isLastRow

            return (
              <DesktopGridItem
                key={`desktop-item-${index}`}
                style={slideItemData.style}
                shouldShowLine={shouldShowLine}
              >
                <DesktopSliderItem
                  slide={slideItemData as ListItemBuilder}
                  style={slideItemData.style}
                  className="desktop-grid-item"
                />
              </DesktopGridItem>
            )
          })}
        </DesktopGrid>
      </Slider>

      {/* Mobile Controls - Only shown on mobile */}
      <StyledControlsContainer>
        <SliderButtonStyle
          onClick={prev}
          disabledPrev={currentSlideIndex === 0}
        >
          <ControlsPrevIcon /> {translate("PREV")}
        </SliderButtonStyle>
        <SliderButtonStyle
          onClick={next}
          disabledNext={currentSlideIndex === lastPossibleSlideIndex}
        >
          {translate("NEXT")} <ControlsNextIcon />
        </SliderButtonStyle>
      </StyledControlsContainer>
      <ButtonsContainer>
        {secondBottomButtonText && secondBottomButtonLink && (
          <QsBottomButton>
            <InvertedButtonLink to={secondBottomButtonLink}>
              {secondBottomButtonText}
            </InvertedButtonLink>
          </QsBottomButton>
        )}
        {bottomButtonText && bottomButtonLink && (
          <QsBottomButton>
            <BasicButtonLink to={bottomButtonLink}>
              {bottomButtonText}
            </BasicButtonLink>
          </QsBottomButton>
        )}
      </ButtonsContainer>
    </SliderWrapper>
  )
}

export default RevealList

const QsBottomButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${BasicButtonLink} {
    margin-top: 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    margin-top: 40px;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    margin-top: 36px;
    flex-direction: column;
    gap: 0px;
  }
`
